import { GrayDeleteButton } from "@components/buttons/gray-delete";
import { PlusButton } from "@components/buttons/plus";
import { RollBackButton } from "@components/buttons/rollback";
import {
  SHBox,
  SHButton,
  SHDataGrid,
  SHDataGridRef,
  SHStack,
} from "@components/design-systems";
import { DefaultDataState } from "@components/design-systems/sh-data-grid/constant";
import { EditSVG } from "@components/svgs";
import { PageRoutes } from "@constants";
import { useRateCard } from "@hooks/useRateCard";
import { TopBar, TopBarContainer } from "@layouts/top-bar";
import {
  RateCardStatus,
  ReadRateCardDTO,
} from "@models/rate-card/entities/rateCard";
import { useTheme } from "@mui/material";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { useMemo, useRef, useState } from "react";
import { generatePath, useNavigate } from "react-router";
import { useUserPermissions } from "@hooks/userUserPermission";
import {
  getAnalysisRateCardListColumns,
  RateCardListNameOfFactory as nameOf,
} from "@pages/platform-analysis-v2/_rate-cards/utils";
import { AnalysisRateCardListConfigs as Configs } from "@pages/platform-analysis-v2/_rate-cards/configs";

export const RateCardsListView = () => {
  const navigate = useNavigate();
  const { palette } = useTheme();
  const ref = useRef<SHDataGridRef | null>(null);
  const fontSize = "13px";
  const { isAdviserGroup } = useUserPermissions();

  const [gridState, setGridState] = useState<Partial<TableState>>({
    ...DefaultDataState,
    sorting: [{ id: nameOf("lastModifiedDate"), desc: true }],
    columnFilters: [
      {
        id: nameOf("status"),
        value: [RateCardStatus.Active],
      },
    ],
  });

  const [changingStatusRateCateIds, setChangingStatusRateCardIds] = useState<
    string[]
  >([]);
  const { updateRateCardStatus } = useRateCard();

  const handleOnChangeStatus = async (
    rateCardId: string,
    status: RateCardStatus,
  ) => {
    setChangingStatusRateCardIds((ids) => [...ids, rateCardId]);
    await updateRateCardStatus(rateCardId, status);
    ref.current?.refreshOdata();
    setChangingStatusRateCardIds((ids) =>
      ids.filter((id) => id !== rateCardId),
    );
  };

  const handleOnEditRateCard = (rateCardId?: string) => {
    navigate(
      generatePath(PageRoutes.platformAnalysisRateCard, {
        rateCardId: rateCardId,
      }),
    );
  };

  const renderItemActions = (itemData: ReadRateCardDTO) => {
    return (
      <SHStack direction="row" justifyContent={"space-between"} spacing={0.5}>
        <SHButton
          sx={{ width: "70%", fontSize: fontSize }}
          startIcon={<EditSVG color={palette.common.white} />}
          variant="contained"
          disabled={false}
          onClick={() => handleOnEditRateCard(itemData.id)}
        >
          Edit
        </SHButton>

        {itemData.status === RateCardStatus.Archived ? (
          <RollBackButton
            disable={changingStatusRateCateIds.includes(itemData.id ?? "")}
            isLoading={changingStatusRateCateIds.includes(itemData.id ?? "")}
            onClick={async () => {
              await handleOnChangeStatus(
                itemData.id ?? "",
                RateCardStatus.Active,
              );
            }}
          />
        ) : (
          <GrayDeleteButton
            disable={changingStatusRateCateIds.includes(itemData.id ?? "")}
            isLoading={changingStatusRateCateIds.includes(itemData.id ?? "")}
            onClick={async () => {
              await handleOnChangeStatus(
                itemData.id ?? "",
                RateCardStatus.Archived,
              );
            }}
          />
        )}
      </SHStack>
    );
  };

  const newColumns: ColumnDef<ReadRateCardDTO, any>[] = useMemo(
    () => getAnalysisRateCardListColumns({ isAdviserGroup, renderItemActions }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdviserGroup],
  );

  return (
    <>
      <TopBarContainer>
        <TopBar
          title={isAdviserGroup ? "Suitability Reviews" : "Platform analysis"}
          tools={
            <>
              <PlusButton
                data-testid="new-supplier-button"
                variant="contained"
                size="extraMedium"
                onClick={() =>
                  navigate(
                    generatePath(PageRoutes.platformAnalysisRateCard, {
                      rateCardId: "new",
                    }),
                  )
                }
              >
                New rate card
              </PlusButton>
            </>
          }
        />
      </TopBarContainer>
      <SHBox sx={{ marginTop: "25px" }}>
        <SHDataGrid
          ref={ref}
          odata={{
            url: Configs.DefaultRoute,
            pickColumns: Configs.PickColumns,
            globalFilterColumns: Configs.GlobalFilterColumns,
          }}
          emptyMessage={"No rate card."}
          columns={newColumns}
          state={gridState}
          onChangeState={setGridState}
          searchBoxProps={{
            placeholder: "Search name, platforms and owners",
          }}
        />
      </SHBox>
    </>
  );
};
